import * as React from 'react';
import { useMemo } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { Location } from 'js/lib/useRouter';

import {
  clearChatSession,
  dropMessages,
  sendMessage,
  setChatSessionId,
  setChatSessionStorageKey,
  setContinuousChatSessionId,
  setContinuousChatSessionStorageKey,
} from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import { getCookieId, getDeviceId } from 'bundles/ai-coach-platform/components/patterns/chat/utils/logoutModeUtils';
import {
  getAuthSessionKeySuffix,
  getChatSessionStorageKey,
  getContinuousChatSessionStorageKey,
  persistentChatSessionId,
  persistentContinuousChatSessionId,
  withAuthSessionKeySuffix,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/sessionUtils';
import type { AiCoachPlatform_RespondToUserMessageMutation as AiCoachPlatformRespondToUserMessageMutation } from 'bundles/ai-coach-platform/queries/__generated__/aiCoachRespondToUserMessageMutation';

type UseCoachTriggerMessageOptions = {
  newSessionOnClick?: boolean;
  onClick?: () => void;
  skipSessionUpdate?: boolean;
  enableContinuousChat?: boolean;
  enableTextStreaming?: boolean;
  enableLoggedOutMode?: boolean;
  location: Location;
} & Pick<
  AiCoachRespondToUserMessageInput,
  'additionalContext' | 'courseraContext' | 'config' | 'responseProcessingType'
>;

/**
 * A hook that handles sending a message to Coach. Handles session state updates and message sending.
 */
const useCoachTriggerMessage = (options: UseCoachTriggerMessageOptions) => {
  const {
    newSessionOnClick,
    onClick,
    skipSessionUpdate = false,
    enableContinuousChat = false,
    enableTextStreaming = false,
    enableLoggedOutMode = false,
    additionalContext,
    courseraContext,
    config,
    responseProcessingType,
    location,
  } = options;

  const cookieId = useMemo(() => (enableLoggedOutMode ? getCookieId() : undefined), [enableLoggedOutMode]);
  const deviceId = useMemo(() => (enableLoggedOutMode ? getDeviceId() : undefined), [enableLoggedOutMode]);

  return React.useCallback<
    ({
      message,
      label,
    }: {
      message?: string;
      label: string;
    }) => Promise<void | AiCoachPlatformRespondToUserMessageMutation | null>
  >(
    ({ message, label }) => {
      if (newSessionOnClick) {
        dropMessages();
        clearChatSession();
      }

      if (onClick) {
        onClick();
      }

      if (!skipSessionUpdate) {
        const authSessionKeySuffix = enableLoggedOutMode ? getAuthSessionKeySuffix() : undefined;

        if (enableContinuousChat) {
          // If continuous chat is being enabled outside of course context (no courseId or courseSlug from location),
          // a uniqueDataString should be provided as the third parameter to getContinuousChatSessionStorageKey
          const courseId = courseraContext?.courseId || undefined;
          const storageKey = withAuthSessionKeySuffix(
            getContinuousChatSessionStorageKey(location, courseId),
            authSessionKeySuffix
          );
          setContinuousChatSessionStorageKey(storageKey);
          setContinuousChatSessionId(persistentContinuousChatSessionId.get(storageKey));
        } else {
          const storageKey = withAuthSessionKeySuffix(getChatSessionStorageKey(location), authSessionKeySuffix);
          setChatSessionStorageKey(storageKey);
          setChatSessionId(persistentChatSessionId.get(storageKey));
        }
      }

      return sendMessage({
        input: {
          message: message ?? label,
          additionalContext,
          courseraContext,
          config,
          responseProcessingType,
        },
        enableTextStreaming,
        enableContinuousChat,
        cookieId,
        deviceId,
      });
    },
    [
      location,
      newSessionOnClick,
      onClick,
      skipSessionUpdate,
      enableContinuousChat,
      enableTextStreaming,
      additionalContext,
      courseraContext,
      config,
      responseProcessingType,
      cookieId,
      deviceId,
      enableLoggedOutMode,
    ]
  );
};

export default useCoachTriggerMessage;
